import { memo, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Home, MessageCircle, Activity, ChevronLeft, ChevronRight } from 'lucide-react';
import { motion } from 'framer-motion';
import { getBrazilianStatus } from '../../utils/detectBrazil';
import { unifiedNavHeaderPtBR } from '../mappings/UnifiedNavHeader-pt-br';

const UnifiedNavHeader = memo(({ title, subtitle, showBackButton = true, customActions }) => {
  const navigate = useNavigate();
  const location = useLocation();

  // Brazilian detection
  const isBrazilian = getBrazilianStatus();

  // Translation function
  const t = (text) => {
    if (!isBrazilian) return text;
    return unifiedNavHeaderPtBR[text] || text;
  };
  
  // Build breadcrumb trail
  const getBreadcrumbs = useCallback(() => {
    const path = location.pathname;
    const crumbs = [];

    // Always start with home
    crumbs.push({ label: t('Home'), path: '/home', icon: Home });

    // Add current page
    if (title && path !== '/home' && path !== '/sports-hub') {
      crumbs.push({ label: title, path: path, current: true });
    }

    return crumbs;
  }, [location.pathname, title, isBrazilian]);
  
  const breadcrumbs = getBreadcrumbs();
  
  return (
    <div className="sticky top-0 z-40 bg-black/60 backdrop-blur-lg border-b border-white/[0.05]">
      <div className="max-w-7xl mx-auto">
        {/* Mobile Header */}
        <div className="md:hidden">
          <div className="flex items-center justify-between px-4 py-3">
            {showBackButton && breadcrumbs.length > 1 && (
              <button
                onClick={() => navigate(breadcrumbs[breadcrumbs.length - 2].path)}
                className="p-2 -ml-2 rounded-md bg-white/[0.02] hover:bg-white/[0.05] border border-white/[0.05] hover:border-white/[0.08] transition-all duration-200"
                aria-label={t('Back')}
              >
                <ChevronLeft className="w-5 h-5 text-white/70" />
              </button>
            )}

            <div className="flex-1 text-center">
              <h1 className="text-white/90 text-sm font-medium">{title}</h1>
              {subtitle && (
                <p className="text-white/50 text-xs">{subtitle}</p>
              )}
            </div>

            {/* Quick Actions */}
            <div className="flex items-center gap-2">
              <button
                onClick={() => navigate('/chat')}
                className="p-2 rounded-md bg-white/[0.02] hover:bg-white/[0.05] border border-white/[0.05] hover:border-violet-500/20 transition-all duration-200"
                aria-label={t('Chat with AI')}
              >
                <MessageCircle className="w-5 h-5 text-white/70 hover:text-violet-400/70" />
              </button>
              {customActions}
            </div>
          </div>
        </div>
        
        {/* Desktop Header */}
        <div className="hidden md:block">
          <div className="px-6 py-3">
            {/* Single row with breadcrumbs and actions */}
            <div className="flex items-center justify-between">
              {/* Breadcrumbs */}
              <div className="flex items-center gap-2">
                {breadcrumbs.map((crumb, index) => {
                  const Icon = crumb.icon;
                  return (
                    <div key={crumb.path} className="flex items-center gap-2">
                      {index > 0 && (
                        <ChevronRight className="w-3.5 h-3.5 text-white/30" />
                      )}
                      <button
                        onClick={() => !crumb.current && navigate(crumb.path)}
                        disabled={crumb.current}
                        className={`
                          flex items-center gap-1.5 px-3 py-1.5 rounded-md transition-all duration-200
                          ${crumb.current
                            ? 'text-emerald-400/80 bg-emerald-500/10 border border-emerald-500/20 cursor-default'
                            : 'text-white/70 hover:text-white/90 bg-white/[0.02] border border-white/[0.05] hover:bg-white/[0.03] hover:border-white/[0.08]'
                          }
                        `}
                      >
                        {Icon && <Icon className="w-3.5 h-3.5" />}
                        <span className="text-xs font-medium tracking-wide uppercase">{crumb.label}</span>
                      </button>
                    </div>
                  );
                })}
              </div>

              {/* Desktop Quick Actions */}
              <div className="flex items-center gap-3">
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => navigate('/chat')}
                  className="flex items-center gap-2 px-4 py-2 rounded-md bg-gradient-to-r from-emerald-500/80 to-violet-500/80 hover:from-emerald-500 hover:to-violet-500 backdrop-blur-sm transition-all duration-200 shadow-lg shadow-emerald-500/10"
                >
                  <MessageCircle className="w-4 h-4 text-white" />
                  <span className="text-[11px] font-medium tracking-[0.15em] uppercase text-white">{t('Chat with AI')}</span>
                </motion.button>
                {customActions}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});

UnifiedNavHeader.displayName = 'UnifiedNavHeader';

export default UnifiedNavHeader;